// BLOQ Talk — Cipher Lock (PQC Path 2): PQXDH-style hybrid E2EE for 1:1 chats + group cipher.
// PORTED from the mobile `frontend/src/utils/cipherlock.ts` (GET /api/admin/docs/cl-cipherlock,
// re-pulled 2026-09-25 for the multi-device wraps — cc_20/cc_25). Web adaptations (per cl-readme):
// dropped `import "./cryptoPolyfill"`, repointed `api` (./clApi) and `storage` (./clStorage, IndexedDB),
// quantum-mixed identity seeding, and a key-arrival emitter so open threads re-decorate locked bubbles.
// Algorithms, encodings, HKDF salts/infos and envelope shapes are byte-identical to mobile.
import { ml_kem768 } from "@noble/post-quantum/ml-kem.js";
import { ml_dsa65 } from "@noble/post-quantum/ml-dsa.js";
import { x25519 } from "@noble/curves/ed25519.js";
import { hkdf } from "@noble/hashes/hkdf.js";
import { sha256 } from "@noble/hashes/sha2.js";
import { xchacha20poly1305 } from "@noble/ciphers/chacha.js";
import { api } from "./clApi";
import { storage } from "./clStorage";
import { b64encode, b64decode, toHex, concat, utf8 } from "./pqc";
import { seedBytes } from "./quantumEntropy";

let identity = null;
let identityUserId = null;
const keyCache = new Map(); // sid -> 32-byte session key
const keyListeners = new Set(); // cb({ sid }) — fired whenever a NEW session key lands on this device

/** Subscribe to key arrivals (unwrap / derivation / mint) so UIs can re-decrypt locked bubbles. */
export function onKeyArrival(cb) {
  keyListeners.add(cb);
  return () => keyListeners.delete(cb);
}

// Drop all in-memory key material (called on logout, alongside wipeCryptoStorage()).
export function resetCipherState() {
  identity = null;
  identityUserId = null;
  keyCache.clear();
}

function utf8Decode(bytes) {
  if (typeof TextDecoder !== "undefined") return new TextDecoder().decode(bytes);
  let out = "";
  let i = 0;
  while (i < bytes.length) {
    const b = bytes[i++];
    if (b < 0x80) out += String.fromCharCode(b);
    else if (b < 0xe0) out += String.fromCharCode(((b & 0x1f) << 6) | (bytes[i++] & 0x3f));
    else if (b < 0xf0) out += String.fromCharCode(((b & 0x0f) << 12) | ((bytes[i++] & 0x3f) << 6) | (bytes[i++] & 0x3f));
    else {
      const cp = ((b & 0x07) << 18) | ((bytes[i++] & 0x3f) << 12) | ((bytes[i++] & 0x3f) << 6) | (bytes[i++] & 0x3f);
      out += String.fromCodePoint(cp);
    }
  }
  return out;
}

function concat3(a, b, c) {
  return concat(concat(a, b), c);
}

function bundleFp(ikPub, pqkPub) {
  return toHex(sha256(concat(ikPub, pqkPub))).slice(0, 16);
}

/** Canonical bytes every device signs (and every peer verifies) for a bundle. */
function bundleSignMsg(ikPubB64, pqkPubB64, fp) {
  return utf8(`bloq-cl-bundle:${ikPubB64}:${pqkPubB64}:${fp}`);
}

function pairCode(fpA, fpB) {
  const [x, y] = [fpA, fpB].sort();
  const h = toHex(sha256(utf8(`bloq-cl-pair:${x}:${y}`))).slice(0, 12).toUpperCase();
  return `${h.slice(0, 4)}-${h.slice(4, 8)}-${h.slice(8, 12)}`;
}

function deriveSessionKey(dh1, dh2, kemSS, chatId) {
  return hkdf(sha256, concat3(dh1, dh2, kemSS), utf8("bloq-cipherlock-v1"), utf8(chatId), 32);
}

async function loadIdentity(userId) {
  if (identity && identityUserId === userId) return identity;
  keyCache.clear();
  const key = `bloq_cl_id_${userId}`;
  const raw = await storage.secureGet(key, null);
  let ikPriv;
  let seed;
  let dsaSeed;
  let quantumSeeded = false;
  if (raw) {
    const parsed = JSON.parse(raw);
    ikPriv = b64decode(parsed.ik);
    seed = b64decode(parsed.seed);
    quantumSeeded = parsed.q === true;
    if (parsed.dsa) {
      dsaSeed = b64decode(parsed.dsa);
    } else {
      // migration: older identities predate ML-DSA — add a signing seed (fp unchanged)
      dsaSeed = (await seedBytes(32)).bytes;
      await storage.secureSet(key, JSON.stringify({ ik: parsed.ik, seed: parsed.seed, dsa: b64encode(dsaSeed), q: parsed.q === true }));
    }
  } else {
    // Fresh Cipher Lock identity: seed the X25519 IK + ML-KEM-768 + ML-DSA-65 keygen with
    // quantum-mixed entropy — HKDF(localCSPRNG ‖ QeM quantum), fail-open to pure local CSPRNG.
    const s1 = await seedBytes(32);
    const s2 = await seedBytes(64);
    const s3 = await seedBytes(32);
    ikPriv = s1.bytes;
    seed = s2.bytes;
    dsaSeed = s3.bytes;
    quantumSeeded = s1.quantum && s2.quantum && s3.quantum;
    await storage.secureSet(key, JSON.stringify({ ik: b64encode(ikPriv), seed: b64encode(seed), dsa: b64encode(dsaSeed), q: quantumSeeded }));
  }
  const ikPub = x25519.getPublicKey(ikPriv);
  const { publicKey: pqkPub, secretKey: pqkSec } = ml_kem768.keygen(seed);
  const { publicKey: dsaPub, secretKey: dsaSec } = ml_dsa65.keygen(dsaSeed);
  identity = { ikPriv, ikPub, pqkSec, pqkPub, dsaSec, dsaPub, fp: bundleFp(ikPub, pqkPub), quantumSeeded };
  identityUserId = userId;
  return identity;
}

/** Ensure this device has an identity bundle and it is published (ML-DSA-65 signed). */
export async function ensureCipherIdentity(userId) {
  const id = await loadIdentity(userId);
  const ikB64 = b64encode(id.ikPub);
  const pqkB64 = b64encode(id.pqkPub);
  const sig = ml_dsa65.sign(bundleSignMsg(ikB64, pqkB64, id.fp), id.dsaSec);
  await api("/pqc/bundle", {
    method: "POST",
    body: { ik_pub: ikB64, pqk_pub: pqkB64, fp: id.fp, sig_pub: b64encode(id.dsaPub), sig: b64encode(sig) },
  });
}

/** This device's Cipher Lock fingerprint (used to hide "Forget" on the local device row). */
export async function myDeviceFp(userId) {
  return (await loadIdentity(userId)).fp;
}

async function storeKey(userId, sid, key) {
  keyCache.set(sid, key);
  await storage.secureSet(`bloq_cl_sk_${userId}_${sid}`, b64encode(key));
  keyListeners.forEach((cb) => { try { cb({ sid }); } catch { /* listener error must not break crypto */ } });
}

async function loadStoredKey(userId, sid) {
  const cached = keyCache.get(sid);
  if (cached) return cached;
  const raw = await storage.secureGet(`bloq_cl_sk_${userId}_${sid}`, null);
  if (!raw) return null;
  const key = b64decode(raw);
  keyCache.set(sid, key);
  return key;
}

// Wrap KEK domains: "group" = BLOQ group key, "dm" = 1:1 session key fan-out to other devices (cc_20).
// KEK = HKDF-SHA256(DH1 ‖ DH2 ‖ KEM, salt, info=`${scopeId}:${targetUserId}`, 32)
const WRAP_SALT = { group: "bloq-cipherlock-group-v1", dm: "bloq-cipherlock-dm-v1" };

function deriveWrapKek(dh1, dh2, kemSS, scope, scopeId, memberId) {
  return hkdf(sha256, concat3(dh1, dh2, kemSS), utf8(WRAP_SALT[scope]), utf8(`${scopeId}:${memberId}`), 32);
}

/** Open a wrap addressed to THIS device (responder_fp === me.fp); null if it doesn't fit. */
function unwrapKey(me, w, scope, scopeId, memberId) {
  try {
    const dh1 = x25519.getSharedSecret(me.ikPriv, b64decode(w.initiator_ik_pub));
    const dh2 = x25519.getSharedSecret(me.ikPriv, b64decode(w.eph_pub));
    const kemSS = ml_kem768.decapsulate(b64decode(w.kem_ct), me.pqkSec);
    const kek = deriveWrapKek(dh1, dh2, kemSS, scope, scopeId, memberId);
    return xchacha20poly1305(kek, b64decode(w.key_n)).decrypt(b64decode(w.key_ct));
  } catch {
    return null;
  }
}

function makeWrap(me, scope, scopeId, memberId, bundle, groupKey) {
  const ephPriv = globalThis.crypto.getRandomValues(new Uint8Array(32));
  const peerIk = b64decode(bundle.ik_pub);
  const dh1 = x25519.getSharedSecret(me.ikPriv, peerIk);
  const dh2 = x25519.getSharedSecret(ephPriv, peerIk);
  const { cipherText, sharedSecret } = ml_kem768.encapsulate(b64decode(bundle.pqk_pub));
  const kek = deriveWrapKek(dh1, dh2, sharedSecret, scope, scopeId, memberId);
  const keyN = globalThis.crypto.getRandomValues(new Uint8Array(24));
  const keyCt = xchacha20poly1305(kek, keyN).encrypt(groupKey);
  return {
    user_id: memberId,
    initiator_ik_pub: b64encode(me.ikPub),
    eph_pub: b64encode(x25519.getPublicKey(ephPriv)),
    kem_ct: b64encode(cipherText),
    key_n: b64encode(keyN),
    key_ct: b64encode(keyCt),
    responder_fp: bundle.fp,
  };
}

/** Obtain the key for a session: stored copy, responder-side derivation, or my per-device wrap. */
async function resolveSessionKey(userId, me, s) {
  const stored = await loadStoredKey(userId, s.id);
  if (stored) return stored;
  if (s.responder_id === userId && s.responder_fp === me.fp) {
    // this device IS the PQXDH target — derive directly
    try {
      const ikA = b64decode(s.initiator_ik_pub);
      const dh1 = x25519.getSharedSecret(me.ikPriv, ikA);
      const dh2 = x25519.getSharedSecret(me.ikPriv, b64decode(s.eph_pub));
      const kemSS = ml_kem768.decapsulate(b64decode(s.kem_ct), me.pqkSec);
      const key = deriveSessionKey(dh1, dh2, kemSS, s.chat_id);
      await storeKey(userId, s.id, key);
      return key;
    } catch {
      return null;
    }
  }
  // any other device (my other login, or the initiator's second device): a key-holder may
  // have wrapped the session key for this exact device identity (cc_20)
  const mine = (s.my_wraps ?? []).find((w) => w.responder_fp === me.fp);
  if (!mine) return null;
  const key = unwrapKey(me, mine, "dm", s.chat_id, userId);
  if (key) await storeKey(userId, s.id, key);
  return key;
}

/** Wrap a session key for every device in `targets` (fan-out helper for 1:1 chats). */
function dmWraps(me, chatId, key, targets) {
  return targets
    .filter((d) => d.user_id && d.ik_pub && d.pqk_pub && d.fp && d.fp !== me.fp)
    .map((d) => {
      try { return makeWrap(me, "dm", chatId, d.user_id, d, key); } catch { return null; }
    })
    .filter(Boolean);
}

/**
 * Ensure a usable Cipher Lock session for a 1:1 chat.
 * Loads every readable session key (derive if PQXDH target, else unwrap my per-device wrap),
 * heals wraps for devices that still can't open a session I hold, then adopts the latest
 * session or — unless `healOnly` — establishes a fresh one against the peer's bundle.
 */
export async function ensureChatCipher(userId, chatId, opts = {}) {
  try {
    const me = await loadIdentity(userId);
    const res = await api(`/chats/${chatId}/cipherlock`);
    let unlocked = 0;
    for (const s of res.sessions) {
      const had = !!(await loadStoredKey(userId, s.id));
      const key = await resolveSessionKey(userId, me, s);
      if (key && !had) unlocked += 1; // a key just arrived (wrap / derivation) → history opens
      // heal: I hold this session's key → wrap it for every live device of either member that
      // can't open it yet. The server pushes `cipherlock.session` so those devices unlock live.
      const targets = key ? dmWraps(me, chatId, key, s.needs_wrap ?? []) : [];
      if (targets.length) {
        api(`/chats/${chatId}/cipherlock/wraps`, { method: "POST", body: { session_id: s.id, wraps: targets } }).catch(() => {});
      }
    }
    const code = res.peer_bundle
      ? pairCode(me.fp, bundleFp(b64decode(res.peer_bundle.ik_pub), b64decode(res.peer_bundle.pqk_pub)))
      : undefined;
    // Verify the peer's ML-DSA-65 bundle signature (detects server-side key substitution).
    let peerSigned = false;
    if (res.peer_bundle?.sig_pub && res.peer_bundle?.sig) {
      try {
        peerSigned = ml_dsa65.verify(
          b64decode(res.peer_bundle.sig),
          bundleSignMsg(res.peer_bundle.ik_pub, res.peer_bundle.pqk_pub, res.peer_bundle.fp),
          b64decode(res.peer_bundle.sig_pub),
        );
      } catch {
        peerSigned = false;
      }
    }
    const latest = res.sessions[0];
    if (latest && keyCache.has(latest.id)) {
      return { state: "active", sessionId: latest.id, fingerprint: code, signed: peerSigned, unlocked };
    }
    if (!res.peer_bundle) return { state: "pending", unlocked };
    // heal-only (background frames / heal-queue, cc_25): unlock + wrap what we can, but NEVER
    // mint a session off-screen — a device that can't open the latest session would otherwise
    // answer every `cipherlock.session` frame with a fresh session (ping-pong).
    if (opts.healOnly) return { state: "pending", unlocked };
    // establish a fresh session as initiator (PQXDH hybrid agreement)
    try {
      const ephPriv = globalThis.crypto.getRandomValues(new Uint8Array(32));
      const ephPub = x25519.getPublicKey(ephPriv);
      const peerIk = b64decode(res.peer_bundle.ik_pub);
      const dh1 = x25519.getSharedSecret(me.ikPriv, peerIk);
      const dh2 = x25519.getSharedSecret(ephPriv, peerIk);
      const { cipherText, sharedSecret } = ml_kem768.encapsulate(b64decode(res.peer_bundle.pqk_pub));
      const key = deriveSessionKey(dh1, dh2, sharedSecret, chatId);
      // fan-out up front: every OTHER live device of both members gets a wrap, so the peer's
      // phone / my other login read this chat from message one
      const peerFp = res.peer_bundle.fp;
      const others = (res.devices ?? []).filter((d) => !(d.user_id === res.peer_id && d.fp === peerFp));
      const created = await api(`/chats/${chatId}/cipherlock`, {
        method: "POST",
        body: {
          initiator_ik_pub: b64encode(me.ikPub),
          eph_pub: b64encode(ephPub),
          kem_ct: b64encode(cipherText),
          responder_fp: peerFp,
          initiator_fp: me.fp,
          wraps: dmWraps(me, chatId, key, others),
        },
      });
      await storeKey(userId, created.id, key);
      return { state: "active", sessionId: created.id, fingerprint: code, signed: peerSigned, unlocked };
    } catch (e) {
      // peer's published bundle is unusable (corrupted / non-KEM key) — stay pending
      // until they republish a valid bundle, instead of disabling the pill entirely
      console.warn("[cipherlock] peer bundle unusable, staying pending:", e);
      return { state: "pending", unlocked };
    }
  } catch (e) {
    console.warn("[cipherlock] ensureChatCipher failed:", e);
    return { state: "off" };
  }
}

/** Encrypt outgoing text under a session key (sync — key must be in memory). */
export function encryptText(sid, text) {
  const key = keyCache.get(sid);
  if (!key) return null;
  const nonce = globalThis.crypto.getRandomValues(new Uint8Array(24));
  const ct = xchacha20poly1305(key, nonce).encrypt(utf8(text));
  return { v: "cl1", sid, n: b64encode(nonce), ct: b64encode(ct) };
}

/** Encrypt a structured payload (text + optional image/voice). v="cl2" marks JSON content. */
export function encryptPayload(sid, payload) {
  if (!payload.image && !payload.voice) return encryptText(sid, payload.text);
  const key = keyCache.get(sid);
  if (!key) return null;
  const nonce = globalThis.crypto.getRandomValues(new Uint8Array(24));
  const ct = xchacha20poly1305(key, nonce).encrypt(
    utf8(JSON.stringify({
      t: payload.text, i: payload.image, s: payload.allow_save !== false,
      ...(payload.voice ? { a: payload.voice, d: payload.voice_dur, w: payload.voice_wave } : {}),
    })),
  );
  return { v: "cl2", sid, n: b64encode(nonce), ct: b64encode(ct) };
}

/** Decrypt an incoming blob; null when the key isn't available on this device. */
export async function decryptBlob(userId, blob) {
  const key = await loadStoredKey(userId, blob.sid);
  if (!key) return null;
  try {
    const pt = xchacha20poly1305(key, b64decode(blob.n)).decrypt(b64decode(blob.ct));
    return utf8Decode(pt);
  } catch {
    return null;
  }
}

/** Decrypt a blob into a structured payload (handles both cl1 text and cl2 JSON). */
export async function decryptPayload(userId, blob) {
  const pt = await decryptBlob(userId, blob);
  if (pt === null) return null;
  if (blob.v === "cl2") {
    try {
      const o = JSON.parse(pt);
      return { text: o.t ?? "", image: o.i ?? null, allow_save: o.s !== false, voice: o.a ?? null, voice_dur: o.d, voice_wave: o.w };
    } catch {
      return { text: pt };
    }
  }
  return { text: pt };
}

/** Dilithium status for the Quantum Shield UI — reflects this device's signing identity. */
export async function getDilithiumInfo(userId) {
  const me = await loadIdentity(userId);
  return { active: me.dsaPub.length > 0, fp: me.fp, quantumSeeded: !!me.quantumSeeded };
}

// ── Group Cipher Lock (BLOQs) — sender-keys style ──────────────────────────
// A random 32-byte group key encrypts all bloq messages (XChaCha20-Poly1305).
// The enabling member wraps that key for every member DEVICE via a per-device PQXDH
// KEK (X25519 IK + ephemeral + ML-KEM-768 encapsulation). The server stores
// only public headers and wrapped ciphertext — it can never unwrap.

/**
 * Ensure this device can use the bloq's group key(s): unwrap my per-device wrap for every
 * session version I can, and heal wraps for member DEVICES that lack one (old versions too,
 * so rotated history stops showing "key not on this device yet"). Never mints — enabling is
 * an explicit owner action (`enableGroupCipher`), so this is safe to run from background frames.
 */
export async function ensureGroupCipher(userId, bloqId) {
  try {
    const me = await loadIdentity(userId);
    const res = await api(`/circles/${bloqId}/cipherlock`);
    const sessions = res.sessions?.length ? res.sessions : res.session ? [res.session] : [];
    if (sessions.length === 0) return { state: res.enabled ? "waiting" : "available", enabled: res.enabled, unlocked: 0 };
    let latestKey = null;
    let unlocked = 0;
    for (const s of sessions) {
      let key = await loadStoredKey(userId, s.id);
      if (!key) {
        const mine = (s.my_wraps?.length ? s.my_wraps : s.wrap ? [s.wrap] : []).find((w) => w.responder_fp === me.fp);
        if (mine) {
          key = unwrapKey(me, mine, "group", bloqId, userId);
          if (key) {
            await storeKey(userId, s.id, key);
            unlocked += 1;
          }
        }
      }
      if (s.id === sessions[0].id) latestKey = key;
      if (!key) continue;
      // heal: wrap this session's key for every member DEVICE that lacks one
      let targets = (s.needs_wrap ?? []).filter((d) => d.user_id && d.ik_pub && d.pqk_pub && d.fp);
      if (!s.needs_wrap) {
        // older backend — fall back to the member-level heal (joined-late or stale identity)
        const stale = new Set(res.stale_for ?? []);
        targets = (res.member_ids || [])
          .filter((m) => (!(s.wrapped_for || []).includes(m) || stale.has(m)) && res.member_bundles[m])
          .map((m) => ({ user_id: m, ...res.member_bundles[m] }));
      }
      if (targets.length) {
        const wraps = targets.map((d) => makeWrap(me, "group", bloqId, d.user_id, d, key));
        api(`/circles/${bloqId}/cipherlock/wraps`, { method: "POST", body: { session_id: s.id, wraps } }).catch(() => {});
      }
    }
    if (!latestKey) return { state: "waiting", enabled: true, sessionId: sessions[0].id, unlocked };
    return { state: "active", enabled: true, sessionId: sessions[0].id, unlocked };
  } catch (e) {
    console.warn("[cipherlock] ensureGroupCipher failed:", e);
    return { state: "off" };
  }
}

/** Owner enables Group Cipher Lock: generate the group key and wrap it for all members. */
export async function enableGroupCipher(userId, bloqId) {
  const me = await loadIdentity(userId);
  const res = await api(`/circles/${bloqId}/cipherlock`);
  if (!res.member_bundles[userId]) await ensureCipherIdentity(userId);
  const groupKey = globalThis.crypto.getRandomValues(new Uint8Array(32));
  const myBundle = res.member_bundles[userId] ?? {
    ik_pub: b64encode(me.ikPub), pqk_pub: b64encode(me.pqkPub), fp: me.fp,
  };
  const wraps = [
    makeWrap(me, "group", bloqId, userId, myBundle, groupKey),
    ...Object.entries(res.member_bundles)
      .filter(([uid]) => uid !== userId)
      .map(([uid, b]) => makeWrap(me, "group", bloqId, uid, b, groupKey)),
  ];
  const created = await api(`/circles/${bloqId}/cipherlock`, { method: "POST", body: { wraps } });
  await storeKey(userId, created.id, groupKey);
  return { state: "active", enabled: true, sessionId: created.id };
}

/**
 * Deterministic group safety number — mobile v1.2.4 spec, byte-identical (vector-verified 5/5).
 * inputs = per-USER fps from member_bundles (no-bundle members excluded). dedupe → sort lexicographically
 * → SHA-256("bloq-cl-group:" + circle_id + ":" + fps.join(":")) → first 12 hex → UPPERCASE → XXXX-XXXX-XXXX.
 * Covers IDENTITIES (changes on join/leave/rotation), NOT the session key. Recompute on every fetch.
 */
export function groupSafetyNumber(circleId, fps) {
  const uniq = [...new Set((fps || []).filter(Boolean))].sort();
  if (!circleId || uniq.length === 0) return null;
  const h = toHex(sha256(utf8(`bloq-cl-group:${circleId}:${uniq.join(":")}`))).slice(0, 12).toUpperCase();
  return `${h.slice(0, 4)}-${h.slice(4, 8)}-${h.slice(8, 12)}`;
}

/**
 * Read-only roster for the group Cipher Lock verification view (Phase 2b).
 * Returns each member's identity fingerprint + whether their published bundle is ML-DSA-65 signed
 * (detects server-side key substitution), and whether the group key is already wrapped for them.
 */
export async function getGroupRoster(userId, bloqId) {
  const me = await loadIdentity(userId);
  const res = await api(`/circles/${bloqId}/cipherlock`);
  const s = res.sessions?.[0] ?? res.session;
  const roster = (res.member_ids || []).map((id) => {
    const b = res.member_bundles?.[id];
    let signed = false;
    if (b?.sig_pub && b?.sig) {
      try {
        signed = ml_dsa65.verify(b64decode(b.sig), bundleSignMsg(b.ik_pub, b.pqk_pub, b.fp), b64decode(b.sig_pub));
      } catch {
        signed = false;
      }
    }
    return { id, fp: b?.fp || null, signed, hasKey: !!s?.wrapped_for?.includes(id), isMe: id === userId };
  });
  const safetyNumber = groupSafetyNumber(bloqId, roster.filter((m) => m.fp).map((m) => m.fp));
  return { enabled: !!res.enabled, sessionId: s?.id || null, roster, myFp: me.fp, safetyNumber };
}
